import { candleDirection, greenRedLabel, nextCandleOf } from "../lib/resolve";
import { sharedDailySource, type RoundSource, type Timeframe } from "../lib/rounds";
import type { BinaryLabel, Round } from "../types";
import type { HistoryEntryInput } from "./historyStore";
import { createRoundLoop, type FetchLike } from "./roundLoop";
import { marketsRoundFilter, useSettingsStore } from "./settingsStore";

/**
 * The Next-Candle Loop, over any timeframe's bank (ADR-0013): read the
 * Context, call the very next candle green or red. Every bank is a barrier
 * bank, so the answer is `forward[0]` on every timeframe and there is one
 * adapter rather than one per bank shape. The weekly and monthly instances
 * live in predictWeeklyStore / predictMonthlyStore, which supply their own
 * persistence keys.
 */

/** Resolve a Next-Candle commitment against the Round's first Forward candle. */
export function resolveNextCandle(
  round: Round,
  guess: BinaryLabel,
): { reveal: null; correct: boolean } {
  return {
    reveal: null,
    correct: guess === candleDirection(nextCandleOf(round)),
  };
}

/** A Next-Candle Loop's history entry: the Green/Red guess against the answer candle. */
function historyNextCandle(timeframe: Timeframe) {
  return (
    round: Round,
    guess: BinaryLabel,
    _reveal: null,
    correct: boolean | undefined,
    note: string | undefined,
  ): HistoryEntryInput => {
    const answer = candleDirection(nextCandleOf(round));
    return {
      mode: "predict",
      timeframe,
      horizon: "nextCandle",
      kind: "free",
      roundId: round.id,
      instrumentId: round.instrumentId,
      instrumentName: round.instrumentName,
      dateRange: round.dateRange,
      choice: greenRedLabel(guess),
      outcome: correct ? "correct" : "incorrect",
      detail: `Closed ${greenRedLabel(answer)}`,
      note,
    };
  };
}

export type NextCandleLoopConfig = {
  timeframe: Timeframe;
  openSource: (baseUrl: string, fetchFn: FetchLike) => Promise<RoundSource<Round>>;
  seenKey: string;
  scoreKey: string;
  streakKey: string;
  /**
   * The eligible-Round filter. Defaults to markets only: this guess charts
   * the whole stored Context, so the Context-length setting is hidden for it
   * (GRA-132) and must not narrow the pool either.
   */
  filter?: () => ReturnType<typeof marketsRoundFilter>;
};

/** Build one timeframe's Next-Candle Loop. */
export function createNextCandleLoop(config: NextCandleLoopConfig) {
  return createRoundLoop({
    openSource: config.openSource,
    seenKey: config.seenKey,
    filter: config.filter ?? marketsRoundFilter,
    resolve: resolveNextCandle,
    scoring: { scoreKey: config.scoreKey, streakKey: config.streakKey },
    history: historyNextCandle(config.timeframe),
    analytics: {
      mode: "predict",
      timeframe: config.timeframe,
      horizon: "nextCandle",
      preset: () => useSettingsStore.getState().preset,
    },
  });
}

export const usePredictDailyNextStore = createNextCandleLoop({
  timeframe: "daily",
  openSource: sharedDailySource,
  seenKey: "seen:daily-next",
  scoreKey: "predict-daily-next:score",
  streakKey: "predict-daily-next:streak",
});
